import { useState, useCallback } from "react";
import Axios from "../../../../../../services/apiFootballService/connection";
import logger from "../../../../../../utils/logger";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../../store/adminData/adminData";
import useChildrenGroups from "../../../../../clubAdmin/hooks/useChildrenGroups";
import { fetchAllGroupTickets } from "../../../../../../services/firebaseService/endPoints/admin/tickets";

export const useContentFormData = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const clubId = groupData?.clubId;
  const [matchList, setMatchList] = useState([]);
  const [ticketsList, setTicketsList] = useState([]);
  const [additionalLocations, setAdditionalLocations] = useState([undefined]);
  const [isLoading, setIsLoading] = useState(false);

  const { childGroupDetails } = useChildrenGroups();
  const [selectedGroups, setSelectedGroups] = useState({
    selectionType: "all",
    selectedValues: null,
    groupIds: null,
  });

  const handleGroupSelectionChange = (groupSelection) => {
    setSelectedGroups({
      selectionType: groupSelection.selectionType || "all",
      selectedValues: groupSelection.selectedValues || null,
      groupIds: groupSelection.groupIds || null,
    });
  };

  const [editConfiguration, setEditConfiguration] = useState({
    imageChanged: false,
  });
  const [oldNotificationInUtc, setOldNotificationInUtc] = useState([null]);
  const [selectedMatchSeason, setSelectedMatchSeason] = useState(null);
  const [matchDate, setMatchDate] = useState(null);
  const [awayTeam, setAwayTeam] = useState(null);
  const [matchName, setMatchName] = useState(null);

  const getTicketsList = useCallback(async () => {
    if (groupData?.id && ticketsList.length === 0) {
      try {
        const response = await fetchAllGroupTickets(groupData?.id, {
          eventType: "current",
          orderDirection: "desc",
        });

        const tickets = response.body.map((ticket) => ({
          id: ticket?.id,
          matchId: ticket?.data?.match?.fixtureId,
          code: ticket?.data?.groupTicket?.eventCode,
          eventDate: ticket?.data?.groupTicket?.eventDate,
          name:
            ticket?.data?.groupTicket?.ticketDetail?.section ||
            ticket?.data?.groupTicket?.eventName ||
            "N/A",
        }));

        setTicketsList(tickets);
      } catch (error) {
        logger.error(error);
      }
    }
  }, [groupData?.id, ticketsList.length]);

  const getMatchList = useCallback(async () => {
    try {
      if (clubId && matchList.length === 0) {
        const response = await Axios.get(`fixtures?team=${clubId}&next=99`);
        const data = response.data.response;

        const res = data.map((fixtureData) => ({
          fixture_id: fixtureData.fixture.id,
          league_id: fixtureData.league.id,
          league: fixtureData.league,
          event_date: fixtureData.fixture.date,
          event_timestamp: fixtureData.fixture.timestamp,
          firstHalfStart: fixtureData.fixture.periods.first,
          secondHalfStart: fixtureData.fixture.periods.second,
          round: fixtureData.league.round,
          status: fixtureData.fixture.status.long,
          statusShort: fixtureData.fixture.status.short,
          elapsed: fixtureData.fixture.status.elapsed,
          venue: fixtureData.fixture.venue.name,
          referee: fixtureData.fixture.referee,
          homeTeam: {
            team_id: fixtureData.teams.home.id,
            team_name: fixtureData.teams.home.name,
            logo: fixtureData.teams.home.logo,
          },
          awayTeam: {
            team_id: fixtureData.teams.away.id,
            team_name: fixtureData.teams.away.name,
            logo: fixtureData.teams.away.logo,
          },
          goalsHomeTeam: fixtureData.goals.home,
          goalsAwayTeam: fixtureData.goals.away,
          score: {
            halftime: fixtureData.score.halftime.home
              ? `${fixtureData.score.halftime.home}-${fixtureData.score.halftime.away}`
              : null,
            fulltime: fixtureData.score.fulltime.home
              ? `${fixtureData.score.fulltime.home}-${fixtureData.score.fulltime.away}`
              : null,
            extratime: fixtureData.score.extratime.home
              ? `${fixtureData.score.extratime.home}-${fixtureData.score.extratime.away}`
              : null,
            penalty: fixtureData.score.penalty.home
              ? `${fixtureData.score.penalty.home}-${fixtureData.score.penalty.away}`
              : null,
          },
        }));

        setMatchList(res);
      }
    } catch (error) {
      setMatchList([]);
    }
  }, [clubId, matchList.length]);

  return {
    matchList,
    setMatchList,
    ticketsList,
    setTicketsList,
    additionalLocations,
    setAdditionalLocations,
    isLoading,
    setIsLoading,
    editConfiguration,
    setEditConfiguration,
    oldNotificationInUtc,
    setOldNotificationInUtc,
    selectedMatchSeason,
    setSelectedMatchSeason,
    matchDate,
    setMatchDate,
    awayTeam,
    setAwayTeam,
    matchName,
    setMatchName,
    getTicketsList,
    getMatchList,
    selectedGroups,
    handleGroupSelectionChange,
    childGroupDetails,
  };
};
